import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getFullPath } from './utils/pathUtils';
import { currentAppPath, forgetSignedInUser, loginPathFor } from './context/signedInState';

const handleLogout = async (message: string, client?: ApolloClient<any>): Promise<never> => {
  // The page this ran out on (an upload link, an approval…): sign-in comes
  // back to it through the address only, since everything stored is forgotten.
  const back = currentAppPath();
  forgetSignedInUser();

  if (client) {
    try {
      await client.clearStore();
    } catch (error) {
      console.error('Error clearing Apollo cache:', error);
    }
  }

  sessionStorage.setItem('authError', message);

  window.location.replace(getFullPath(loginPathFor(back)));
  return new Promise(() => {});
};

const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

const httpLink = createHttpLink({
  uri: `${backendHost}/graphql`,
  credentials: 'include',
});

let apolloClient: ApolloClient<any> | undefined = undefined;

const loginPath = () => getFullPath('/login').replace(/\/$/, '');

const onLoginPage = () => {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return path === loginPath() || path.endsWith('/login');
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // The sign-in page asks `me` to see if a cookie is already there. No session
  // is the normal answer — do not treat it as expiry or the page reloads forever.
  if (onLoginPage() || operation?.operationName === 'GetUserProfile') return;

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const errorMessage = err.message.toLowerCase();
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        errorMessage.includes('jwt expired') ||
        errorMessage === 'not authenticated' ||
        errorMessage.includes('not authenticated') ||
        errorMessage.includes('authentication required')
      ) {
        handleLogout('Your session has expired. Please log in again.', apolloClient);
        return;
      }
    }
  }

  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    handleLogout('You need to log in to access this resource.', apolloClient);
  }
});

export const client = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
});

apolloClient = client;
